<script setup>
import { ref, computed, watch, nextTick } from 'vue';
import { OnClickOutside } from '@vueuse/components';
import { useI18n } from 'vue-i18n';
import ContactAPI from 'dashboard/api/contacts';

const props = defineProps({
  modelValue: {
    type: Array,
    default: () => [],
  },
  placeholder: {
    type: String,
    default: '',
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  message: {
    type: String,
    default: '',
  },
  hasError: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['update:modelValue']);

const { t } = useI18n();

const selectedIds = ref([...props.modelValue]);
const contactCache = ref(new Map());
const open = ref(false);
const search = ref('');
const searchResults = ref([]);
const isSearching = ref(false);
const currentPage = ref(1);
const hasMorePages = ref(false);
const searchInputRef = ref(null);

let debounceTimer = null;

const selectedContacts = computed(() =>
  selectedIds.value.map(id => contactCache.value.get(id)).filter(Boolean)
);

const selectPlaceholder = computed(
  () => props.placeholder || t('CAMPAIGN.CONTACTS_SELECTOR.PLACEHOLDER')
);

const searchContacts = async (query, page = 1) => {
  if (!query || query.length < 1) {
    searchResults.value = [];
    hasMorePages.value = false;
    return;
  }

  isSearching.value = true;
  try {
    const { data } = await ContactAPI.search(query, page, 'name');
    const contacts = data.payload || [];
    const meta = data.meta || {};

    if (page === 1) {
      searchResults.value = contacts;
    } else {
      searchResults.value = [...searchResults.value, ...contacts];
    }

    currentPage.value = page;
    const totalPages = Math.ceil((meta.all_count || contacts.length) / 15);
    hasMorePages.value = page < totalPages;
  } catch {
    searchResults.value = [];
    hasMorePages.value = false;
  } finally {
    isSearching.value = false;
  }
};

const debouncedSearch = query => {
  clearTimeout(debounceTimer);
  debounceTimer = setTimeout(() => {
    searchContacts(query, 1);
  }, 300);
};

const contactDisplayName = contact => {
  const name = contact.name || '';
  const phone = contact.phone_number || '';
  const email = contact.email || '';
  const secondary = phone || email;
  return secondary ? `${name} (${secondary})` : name;
};

const isSelected = contactId => selectedIds.value.includes(contactId);

const toggleContact = contact => {
  const id = contact.id;
  const index = selectedIds.value.indexOf(id);
  if (index === -1) {
    selectedIds.value.push(id);
    contactCache.value.set(id, contact);
  } else {
    selectedIds.value.splice(index, 1);
  }
  emit('update:modelValue', [...selectedIds.value]);
};

const removeContact = id => {
  const index = selectedIds.value.indexOf(id);
  if (index !== -1) {
    selectedIds.value.splice(index, 1);
    emit('update:modelValue', [...selectedIds.value]);
  }
};

const toggleDropdown = () => {
  if (props.disabled) return;
  open.value = !open.value;
  if (open.value) {
    search.value = '';
    searchResults.value = [];
    nextTick(() => searchInputRef.value?.focus());
  }
};

const handleScroll = event => {
  const el = event.target;
  if (
    !isSearching.value &&
    hasMorePages.value &&
    el.scrollTop + el.clientHeight >= el.scrollHeight - 10
  ) {
    searchContacts(search.value, currentPage.value + 1);
  }
};

watch(
  () => props.modelValue,
  newValue => {
    selectedIds.value = [...newValue];
  }
);

watch(search, newValue => {
  debouncedSearch(newValue);
});
</script>

<template>
  <div
    class="relative w-full min-w-0"
    :class="{
      'cursor-not-allowed': disabled,
      'group/combobox': !disabled,
    }"
    @click.prevent
  >
    <OnClickOutside @trigger="open = false">
      <div
        class="flex flex-wrap w-full gap-2 px-3 py-2.5 border rounded-lg cursor-pointer bg-n-alpha-black2 min-h-[42px] transition-all duration-500 ease-in-out"
        :class="{
          'border-n-ruby-8': hasError,
          'border-n-weak dark:border-n-weak hover:border-n-slate-6 dark:hover:border-n-slate-6':
            !hasError && !open,
          'border-n-brand': open,
          'cursor-not-allowed pointer-events-none opacity-50': disabled,
        }"
        @click="toggleDropdown"
      >
        <div
          v-for="contact in selectedContacts"
          :key="contact.id"
          class="flex items-center justify-center max-w-full gap-1 px-2 py-0.5 rounded-lg bg-n-alpha-black1"
          @click.stop
        >
          <span class="flex-grow min-w-0 text-sm truncate text-n-slate-12">
            {{ contactDisplayName(contact) }}
          </span>
          <span
            class="flex-shrink-0 cursor-pointer i-lucide-x size-3 text-n-slate-11"
            @click="removeContact(contact.id)"
          />
        </div>
        <span
          v-if="selectedContacts.length === 0"
          class="flex items-center text-sm text-n-slate-11"
        >
          {{ selectPlaceholder }}
        </span>
      </div>

      <div
        v-show="open"
        class="absolute z-50 w-full mt-1 transition-opacity duration-200 border rounded-md shadow-lg bg-n-solid-1 border-n-strong"
      >
        <div class="relative border-b border-n-strong">
          <span class="absolute i-lucide-search top-2.5 size-4 left-3" />
          <input
            ref="searchInputRef"
            v-model="search"
            type="search"
            :placeholder="t('CAMPAIGN.CONTACTS_SELECTOR.SEARCH_PLACEHOLDER')"
            class="reset-base w-full py-2 pl-10 pr-2 text-sm focus:outline-none border-none rounded-t-md bg-n-solid-1 text-n-slate-12"
          />
        </div>
        <ul
          class="py-1 mb-0 overflow-auto max-h-60"
          role="listbox"
          aria-multiselectable="true"
          @scroll="handleScroll"
        >
          <li
            v-for="contact in searchResults"
            :key="contact.id"
            class="flex items-center justify-between w-full gap-2 px-3 py-2 text-sm transition-colors duration-150 cursor-pointer hover:bg-n-alpha-2"
            :class="{ 'bg-n-alpha-2': isSelected(contact.id) }"
            role="option"
            :aria-selected="isSelected(contact.id)"
            @click="toggleContact(contact)"
          >
            <div class="flex flex-col min-w-0">
              <span
                class="truncate text-n-slate-12"
                :class="{ 'font-medium': isSelected(contact.id) }"
              >
                {{ contact.name || 'Unnamed' }}
              </span>
              <span class="text-xs truncate text-n-slate-11">
                {{ contact.phone_number || contact.email || '' }}
              </span>
            </div>
            <span
              v-if="isSelected(contact.id)"
              class="flex-shrink-0 i-lucide-check size-4 text-n-slate-11"
            />
          </li>
          <li
            v-if="isSearching"
            class="px-3 py-2 text-sm text-n-slate-11 text-center"
          >
            {{ t('CAMPAIGN.CONTACTS_SELECTOR.SEARCHING') }}
          </li>
          <li
            v-else-if="search.length > 0 && searchResults.length === 0"
            class="px-3 py-2 text-sm text-n-slate-11"
          >
            {{ t('CAMPAIGN.CONTACTS_SELECTOR.EMPTY') }}
          </li>
          <li
            v-else-if="search.length === 0"
            class="px-3 py-2 text-sm text-n-slate-11"
          >
            {{ t('CAMPAIGN.CONTACTS_SELECTOR.TYPE_TO_SEARCH') }}
          </li>
        </ul>
      </div>

      <p
        v-if="message"
        class="mt-2 mb-0 text-xs truncate transition-all duration-500 ease-in-out"
        :class="{
          'text-n-ruby-9': hasError,
          'text-n-slate-11': !hasError,
        }"
      >
        {{ message }}
      </p>
    </OnClickOutside>
  </div>
</template>
